import { Mutation } from '@apollo/client/react/components';
import { useMutation } from '@apollo/client';
import React, { Fragment, useState, useMemo, useCallback } from 'react';
import round from 'lodash/round';

import get from 'lodash/get';
import SensorFilters from './SensorFilters';
import { remove, create, sensorsPaginatedQuery, sensorsCountQuery } from '../../../graph/sensors';
import { Text, TruncatedText } from '@/se/components/typography';
import CollectionListOnly from '@/se/components/collection/CollectionListOnly';
import isFinite from 'lodash/isFinite';
import LinkButton from '@/se/components/LinkButton';
import SensorConfiguration from './SensorConfiguration';
import Tooltip from '../../Tooltip';
import MuiTooltip from '@material-ui/core/Tooltip';
import { distanceFromSeconds } from '../gateway/Gateways';
import { isDefinedAndNotNull } from '@/se/utilities/check';
import { withRouter } from 'react-router';
import { DeleteOutline, Edit, Info, Add } from '@material-ui/icons';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';

export const roundFixed = v => (isFinite(v) ? round(v, 1).toFixed(1) : '-');
const MinMax = ({ data: { min, max } }) => <TruncatedText>{`${roundFixed(min)} / ${roundFixed(max)}`}</TruncatedText>;

export const RemoveButton = ({ id }) => (
  <Mutation mutation={remove}>
    {(mutate, { loading }) => (
      <LinkButton danger onClick={() => mutate({ variables: { id } })} disabled={!!loading}>
        <Tooltip content="Remove from list">
          <DeleteOutline fontSize={'small'} />
        </Tooltip>
      </LinkButton>
    )}
  </Mutation>
);

export const ConfigButton = ({ data }) => {
  const [open, setOpen] = useState(false);
  return (
    <Fragment>
      <SensorConfiguration data={data} id={data.id} open={open} setOpen={setOpen} />
      <LinkButton onClick={() => setOpen(true)}>
        <Tooltip content="Edit parameters">
          <Edit />
        </Tooltip>
      </LinkButton>
    </Fragment>
  );
};

export const DeviceInfoButton = ({ data }) => {
  const tooltipContent = (
    <div style={{ textAlign: 'left', lineHeight: 1.5 }}>
      <div>
        <strong>Model:</strong> {data.model || 'Unknown'}
      </div>
      <div>
        <strong>Firmware:</strong> {data.firmwareVersion || 'Unknown'}
      </div>
    </div>
  );
  return (
    <MuiTooltip title={tooltipContent} arrow placement="left">
      <Info fontSize="small" style={{ opacity: 0.5, cursor: 'help' }} />
    </MuiTooltip>
  );
};

export const colorForValue = (value, min, max) => {
  if (!value || value === '-') {
    return undefined;
  } else if ((value <= min && isFinite(min)) || (value >= max && isFinite(max))) {
    return 'red';
  } else {
    return 'lime';
  }
};

export const TEMP_UNIT = {
  Celsius: 'C',
  Fahrenheit: 'F',
};

export const temperatureLabel = unit => {
  if (unit === TEMP_UNIT.Celsius) {
    return '℃';
  } else if (unit === TEMP_UNIT.Fahrenheit) {
    return '℉';
  } else {
    return '';
  }
};

export const currentTempColumn = {
  title: 'Temp',
  lens: data => ({
    value: get(data, 'measurement.temperature.value'),
    unit: get(data, 'measurement.temperature.unit'),
    min: get(data, 'temperatureMin.value'),
    max: get(data, 'temperatureMax.value'),
  }),
  Component: ({ data: { value, unit, min, max } }) => (
    <Text style={{ color: colorForValue(value, min, max) }}>{`${roundFixed(value)} ${temperatureLabel(unit)}`}</Text>
  ),
};

export const currentHumidityColumn = {
  title: 'Humidity',
  lens: data => ({ value: get(data, 'measurement.humidity'), min: data.humidityMin, max: data.humidityMax }),
  Component: ({ data: { value, min, max } }) => (
    <Text style={{ color: colorForValue(value, min, max) }}>{`${roundFixed(value)} %`}</Text>
  ),
};

export const tempMinMaxColumn = {
  title: 'T Min/Max',
  lens: data => ({ min: get(data, 'temperatureMin.value'), max: get(data, 'temperatureMax.value') }),
  Component: MinMax,
};

export const humidityMinMaxColumn = {
  title: 'H Min/Max',
  lens: data => ({ min: data.humidityMin, max: data.humidityMax }),
  Component: MinMax,
};

export const columns = [
  {
    title: 'ID',
    sortKey: 'id',
    lens: data => data.id,
    Component: props => <TruncatedText>{props.data}</TruncatedText>,
  },
  {
    title: 'Location',
    sortKey: 'location',
    lens: data => data.location,
    Component: props => <TruncatedText>{props.data}</TruncatedText>,
  },
  currentTempColumn,
  currentHumidityColumn,
  {
    title: 'Battery',
    sortKey: 'batteryLevel',
    lens: data => get(data, 'measurement.battery'),
    Component: ({ data }) => <TruncatedText>{isFinite(data) ? `${data} %` : '-'}</TruncatedText>,
  },
  {
    title: 'Last Sync',
    sortKey: 'lastSeenAt',
    lens: data => get(data, 'lastSeen'),
    Component: ({ data }) => (
      <TruncatedText>{isDefinedAndNotNull(data) ? `${distanceFromSeconds(data)} ago` : '-'}</TruncatedText>
    ),
  },
  tempMinMaxColumn,
  humidityMinMaxColumn,
  {
    title: 'Info',
    lens: data => data,
    Component: DeviceInfoButton,
  },
  {
    title: '',
    lens: data => data,
    Component: ConfigButton,
  },
];

const AddSensorButton = () => {
  const [open, setOpen] = useState(false);
  const [mac, setMac] = useState('');
  const [error, setError] = useState('');
  const [createSensor, { loading }] = useMutation(create);

  const handleSubmit = useCallback(async () => {
    const normalized = mac.replace(/[:\-\s]/g, '').toUpperCase();
    if (!/^[0-9A-F]{12}$/.test(normalized)) {
      setError('Invalid MAC address. Expected 12 hex characters (e.g. C3000036CA3B).');
      return;
    }
    try {
      await createSensor({
        variables: { mac: normalized },
        refetchQueries: ['sensorsPaginated', 'sensorsCount'],
      });
      setMac('');
      setError('');
      setOpen(false);
    } catch (e) {
      setError(e.message || 'Failed to create sensor');
    }
  }, [mac, createSensor]);

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<Add />}
        onClick={() => setOpen(true)}
        style={{ marginRight: 8 }}
      >
        Add Sensor
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Sensor by MAC</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="MAC Address"
            placeholder="C3000036CA3B or C3:00:00:36:CA:3B"
            value={mac}
            onChange={e => {
              setMac(e.target.value);
              setError('');
            }}
            error={!!error}
            helperText={error}
            disabled={loading}
            onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} color="primary" disabled={loading || !mac.trim()}>
            {loading ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const paginationConfig = {
  query: sensorsPaginatedQuery,
  countQuery: sensorsCountQuery,
  entityKey: 'sensorsPaginated',
  countKey: 'sensorsCount',
  pageSize: 50,
};

const pickFilter = filter => ({
  search: filter?.search || undefined,
  enabled: filter?.enabledStatus === 'enabled' ? true : filter?.enabledStatus === 'disabled' ? false : undefined,
});

const SensorList = ({ isSuperAdmin = false, location, history }) => {
  const [filter, setFilter] = useState({
    search: '',
    enabledStatus: isSuperAdmin ? 'all' : 'enabled',
  });

  // Add enabled status + delete columns for super admin
  const displayColumns = useMemo(() => {
    if (!isSuperAdmin) return columns;

    const enabledColumn = {
      title: 'Status',
      lens: data => data.enabled,
      Component: ({ data }) => (
        <TruncatedText style={{ color: data ? 'lime' : 'orange' }}>{data ? 'Enabled' : 'Disabled'}</TruncatedText>
      ),
    };

    const deleteColumn = {
      title: '',
      lens: data => data,
      Component: ({ data }) => <RemoveButton id={data.id} />,
    };

    return [
      columns[0], // ID
      columns[1], // Location
      enabledColumn,
      ...columns.slice(2),
      deleteColumn,
    ];
  }, [isSuperAdmin]);

  const SensorFilterComponent = useMemo(
    () =>
      ({ value, onChange }) => <SensorFilters value={value} onChange={onChange} isSuperAdmin={isSuperAdmin} />,
    [isSuperAdmin]
  );

  return (
    <CollectionListOnly
      tableKey="SensorList"
      entityName="Sensors"
      columns={displayColumns}
      idProvider={item => item.id}
      getList={data => data?.sensorsPaginated || []}
      paginationConfig={paginationConfig}
      pickFilter={pickFilter}
      defaultSort={{ title: 'Location', type: 'asc' }}
      FilterComponent={SensorFilterComponent}
      filter={filter}
      setFilter={setFilter}
      location={location}
      history={history}
      HeaderLeft={isSuperAdmin ? <AddSensorButton /> : undefined}
    />
  );
};

export default withRouter(SensorList);
